import { paths } from "@kwp/firestore";
import { collection, doc, limit, onSnapshot, orderBy, query, where, type QueryConstraint } from "firebase/firestore";
import { useEffect, useState } from "react";
import { useClients } from "../../lib/clients";
import { useMe } from "../../lib/me";
import { mergeTasks, toTask, type StatusTab, type Task } from "./model";

export type TasksState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; tasks: Task[] };

/** The newest tasks per query; each query uses a composite index in `firestore.indexes.json`. */
const PAGE = 300;

/**
 * Live tasks of one status in one workspace, read straight from Firestore (clients never write
 * domain data; changes go through the api and come back here). The rules allow a member to read
 * workspace-visible tasks plus private ones they created or are assigned, and rules are not
 * filters, so this runs one query per case the rules can prove and merges the results.
 */
export function useTasks(workspaceId: string, status: StatusTab): TasksState {
  const { firebase } = useClients();
  const me = useMe();
  const [state, setState] = useState<TasksState>({ status: "loading" });

  useEffect(() => {
    setState({ status: "loading" });
    const col = collection(firebase.db, paths.tasks(workspaceId));
    const page: QueryConstraint[] = [where("status", "==", status), orderBy("updatedAt", "desc"), limit(PAGE)];
    const queries = [
      query(col, where("visibility", "==", "workspace"), ...page),
      query(col, where("creatorUid", "==", me.uid), ...page),
      query(col, where("assigneeUid", "==", me.uid), ...page),
    ];
    const results: Array<Task[] | null> = queries.map(() => null);
    let failed = false;

    const unsubscribes = queries.map((q, i) =>
      onSnapshot(
        q,
        (snap) => {
          results[i] = snap.docs.flatMap((d) => {
            const task = toTask(d.id, d.data());
            return task === null ? [] : [task];
          });
          if (failed || results.some((r) => r === null)) return;
          setState({ status: "ready", tasks: mergeTasks(...results.map((r) => (r === null ? [] : r))) });
        },
        (error) => {
          failed = true;
          setState({ status: "error", message: error.message });
        },
      ),
    );
    return () => unsubscribes.forEach((u) => u());
  }, [firebase.db, workspaceId, status, me.uid]);

  return state;
}

export type TaskState = { status: "loading" } | { status: "missing" } | { status: "error"; message: string } | { status: "ready"; task: Task };

/**
 * One task, live. A task that does not exist, and a private one the rules do not let this person
 * read, both show as missing: the screen cannot tell them apart, and should not.
 */
export function useTask(workspaceId: string, taskId: string): TaskState {
  const { firebase } = useClients();
  const [state, setState] = useState<TaskState>({ status: "loading" });

  useEffect(() => {
    setState({ status: "loading" });
    return onSnapshot(
      doc(firebase.db, paths.task(workspaceId, taskId)),
      (snap) => {
        const task = snap.exists() ? toTask(snap.id, snap.data()) : null;
        setState(task === null ? { status: "missing" } : { status: "ready", task });
      },
      (error) => setState(error.code === "permission-denied" ? { status: "missing" } : { status: "error", message: error.message }),
    );
  }, [firebase.db, workspaceId, taskId]);

  return state;
}

/** The current time, refreshed every minute so due-date highlighting stays right. */
export function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);
  return now;
}
